import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Ship, Timer, DollarSign, Percent, Wallet, CalendarCheck2 } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { api, money, todayLocal, prettyDate, VEHICLE_STYLES, RES_STYLES } from "./api";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="card-line rounded-2xl p-5" data-testid={testid}>
    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
      <Icon size={13} className="text-aqua" /> {label}
    </p>
    <p className="mt-2 font-display text-2xl font-extrabold text-offwhite sm:text-3xl">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted">{sub}</p>}
  </div>
);

export default function PortalDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/portal/summary", { params: { today: todayLocal() } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load dashboard"));
  }, []);

  if (!data) return <PortalShell title="Dashboard"><p className="py-20 text-center text-sm text-muted">Loading…</p></PortalShell>;

  const available = data.vehicles.filter((v) => v.status === "available").length;

  return (
    <PortalShell title="Dashboard">
      {data.contracts_pending > 0 && (
        <Link
          to="/portal/contracts"
          data-testid="dashboard-contracts-banner"
          className="mb-6 flex items-center gap-3 rounded-xl border border-orange-500/40 bg-orange-400/10 p-4 text-sm font-semibold text-offwhite transition-transform hover:-translate-y-0.5"
        >
          Action required: sign {data.contracts_pending} partnership agreement{data.contracts_pending > 1 ? "s" : ""} before deployment — reservations are locked until then.
        </Link>
      )}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" data-testid="portal-stats">
        <Stat icon={CalendarClock} label="Today's Reservations" value={data.todays_reservations.length} sub={prettyDate(data.today)} testid="stat-today" />
        <Stat icon={Ship} label="Fleet Status" value={`${available}/${data.vehicles.length}`} sub="jet cars available" testid="stat-fleet" />
        <Stat icon={Timer} label="Eligible Hours This Week" value={data.eligible_hours} sub={`${prettyDate(data.week.start)} – ${prettyDate(data.week.end)}`} testid="stat-hours" />
        <Stat icon={DollarSign} label="Gross Rental Revenue" value={money(data.gross_revenue)} sub="this week" testid="stat-gross" />
        <Stat icon={Percent} label="Estimated Share" value={`${data.partner_pct}%`} sub={data.partner_pct === 60 ? "16+ hour tier reached" : "40% tier · 60% at 16+ hrs"} testid="stat-pct" />
        <Stat icon={Wallet} label="Estimated Payout" value={money(data.estimated_payout)} sub="before adjustments" testid="stat-payout" />
        <Stat icon={CalendarCheck2} label="Next Payout Date" value={prettyDate(data.next_payout_date)} sub="Wednesday settlement" testid="stat-next-payout" />
        <Link to="/portal/reservations" className="card-line group grid place-items-center rounded-2xl p-5 text-center" data-testid="stat-new-reservation">
          <span className="font-display text-sm font-bold uppercase tracking-[0.15em] text-aqua">+ New Reservation</span>
        </Link>
      </div>

      <h2 className="mb-4 mt-10 font-display text-lg font-bold text-offwhite">Today's Schedule</h2>
      {data.todays_reservations.length === 0 ? (
        <p className="card-line rounded-xl py-12 text-center text-sm text-muted" data-testid="today-empty">No reservations today yet.</p>
      ) : (
        <div className="space-y-3">
          {data.todays_reservations.map((r) => (
            <Link
              key={r.reservation_id}
              to={`/portal/reservations?focus=${r.reservation_id}`}
              data-testid={`today-res-${r.reservation_id}`}
              className="card-line flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl px-5 py-4 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-mono text-xs text-aqua">{r.reservation_id}</span>
              <span className="font-display text-base font-bold text-offwhite">{r.start_time} – {r.end_time}</span>
              <span className="text-sm text-muted">{r.vehicle_name}</span>
              <span className="text-sm text-muted">{r.customer.name}</span>
              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${RES_STYLES[r.status]}`}>{r.status.replace("_", " ")}</span>
              <span className={`text-xs font-semibold ${r.payment_status === "paid" ? "text-emerald-700" : "text-orange-600"}`}>
                {r.payment_status === "paid" ? "Paid" : "Payment pending"}
              </span>
            </Link>
          ))}
        </div>
      )}

      <h2 className="mb-4 mt-10 font-display text-lg font-bold text-offwhite">Assigned Jet Cars</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.vehicles.map((v) => (
          <div key={v.vehicle_id} className="card-line rounded-xl p-5" data-testid={`vehicle-${v.vehicle_id}`}>
            <div className="flex items-center justify-between gap-3">
              <p className="font-display text-base font-bold text-offwhite">{v.name}</p>
              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${VEHICLE_STYLES[v.status]}`}>
                {v.status.replace("_", " ")}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted">{v.color}</p>
          </div>
        ))}
      </div>
    </PortalShell>
  );
}
